// src/services/eventService.ts
import type {
  Event,
  CreateEventData,
  UpdateEventData,
  EventRegistration,
} from "../types/event";

const API_BASE = import.meta.env.VITE_API_BASE;

// Helper function to get auth token - FIXED to match AuthContext storage
const getAuthToken = (): string | null => {
  if (typeof window !== "undefined") {
    // Check all possible token storage locations used in AuthContext
    const token =
      localStorage.getItem("token") || // Main token storage in AuthContext
      sessionStorage.getItem("token") ||
      localStorage.getItem("accessToken") || // Fallback
      sessionStorage.getItem("accessToken") || // Fallback
      null;

    console.log("🔐 Token lookup:", {
      localStorageToken: !!localStorage.getItem("token"),
      sessionStorageToken: !!sessionStorage.getItem("token"),
      localStorageAccessToken: !!localStorage.getItem("accessToken"),
      sessionStorageAccessToken: !!sessionStorage.getItem("accessToken"),
      foundToken: !!token,
    });

    return token;
  }
  return null;
};

// Enhanced helper function to get auth headers with debugging
const getAuthHeaders = (): HeadersInit => {
  const token = getAuthToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
    console.log(
      "✅ Authorization header added with token:",
      `${token.substring(0, 20)}...`,
    );
  } else {
    console.warn("❌ No token found for authenticated request");
    console.log("Available localStorage keys:", Object.keys(localStorage));
    console.log("Available sessionStorage keys:", Object.keys(sessionStorage));
  }

  return headers;
};

// Enhanced fetch with better error handling and debugging
const authFetch = async (url: string, options: RequestInit = {}) => {
  const headers = getAuthHeaders();

  console.log("🌐 Making authenticated request to:", url);
  console.log("📤 Request headers:", headers);

  const response = await fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  console.log("📨 Response status:", response.status);

  if (!response.ok) {
    const errorText = await response.text();
    console.error("❌ Request failed:", {
      status: response.status,
      statusText: response.statusText,
      url,
      error: errorText,
    });

    let errorMessage = `Request failed with status ${response.status}`;
    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.error || errorData.message || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }

    throw new Error(errorMessage);
  }

  return response;
};

export const eventService = {
  // Event CRUD
  async getEvents(filters?: {
    startDate?: string;
    endDate?: string;
    type?: string;
    audience?: string;
    status?: string;
  }): Promise<Event[]> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);
    if (filters?.type) params.append("type", filters.type);
    if (filters?.audience) params.append("audience", filters.audience);
    if (filters?.status) params.append("status", filters.status);

    const response = await authFetch(`${API_BASE}/events?${params}`);
    return response.json();
  },

  async getEvent(id: string): Promise<Event> {
    const response = await authFetch(`${API_BASE}/events/${id}`);
    return response.json();
  },

  async createEvent(eventData: CreateEventData): Promise<Event> {
    const response = await authFetch(`${API_BASE}/events`, {
      method: "POST",
      body: JSON.stringify(eventData),
    });
    return response.json();
  },

  async updateEvent(id: string, eventData: UpdateEventData): Promise<Event> {
    const response = await authFetch(`${API_BASE}/events/${id}`, {
      method: "PUT",
      body: JSON.stringify(eventData),
    });
    return response.json();
  },

  async deleteEvent(id: string): Promise<void> {
    await authFetch(`${API_BASE}/events/${id}`, {
      method: "DELETE",
    });
  },

  // Event Registration
  async registerForEvent(eventId: string): Promise<EventRegistration> {
    const response = await authFetch(`${API_BASE}/events/${eventId}/register`, {
      method: "POST",
    });
    return response.json();
  },

  async unregisterFromEvent(eventId: string): Promise<void> {
    await authFetch(`${API_BASE}/events/${eventId}/unregister`, {
      method: "DELETE",
    });
  },

  async getEventRegistrations(eventId: string): Promise<EventRegistration[]> {
    const response = await authFetch(
      `${API_BASE}/events/${eventId}/registrations`,
    );
    return response.json();
  },

  async getMyRegistrations(): Promise<EventRegistration[]> {
    const response = await authFetch(
      `${API_BASE}/events/user/my-registrations`,
    );
    return response.json();
  },
};
